import React, { useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  DatePicker,
  Empty,
  Form,
  Image,
  Input,
  List,
  Modal,
  Segmented,
  Space,
  Tag,
  Typography
} from "antd";
import {
  CameraOutlined,
  DeleteOutlined,
  EditOutlined,
  FilePdfOutlined,
  LinkOutlined,
  MailOutlined,
  PictureOutlined,
  PlusOutlined,
  WarningOutlined
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { Success } from "../../../commons/Messages/Messages";
import { openBlankTab, openFile } from "../../../native/files";
import { SignatureForm } from "../workReports/SignDialog";
import "../workReports/workReports.css";
import { shrinkPhoto } from "../workReports/photos";
import {
  createNotice,
  emailNotice,
  linkPhoto,
  listSiteDocPhotos,
  noticePdf,
  photoUrl,
  unlinkPhoto,
  uploadPhoto
} from "./api";
import DictationField from "./DictationField";

const dateTime = value => (value ? dayjs(value).format("DD.MM.YYYY HH:mm") : "");

// ---------------------------------------------------------------------------------------------
// Behinderungen / Bedenken
// ---------------------------------------------------------------------------------------------

const NOTICE_TITLES = { OBSTRUCTION: "Behinderungsanzeige", CONCERN: "Bedenkenanzeige" };

/** Creates the formal letter of a saved obstruction (once) and sends it. */
const NoticeActions = ({ entryId, obstruction, clientEmail, disabled, onChanged }) => {
  const { t } = useTranslation("dashboard");
  const [dialog, setDialog] = useState(null); // create | email
  const [busy, setBusy] = useState(false);
  const [form] = Form.useForm();
  const notice = obstruction.notice;
  const title = NOTICE_TITLES[obstruction.kind];

  const openPdf = async () => {
    const tab = openBlankTab();
    try {
      const { blob, fileName } = await noticePdf(entryId, obstruction._id);
      await openFile(blob, { tab, fileName });
    } catch (error) {
      tab?.close();
      generalErrorAlert(error);
    }
  };

  const submit = async () => {
    const values = await form.validateFields();
    setBusy(true);
    try {
      if (dialog === "create") {
        const res = await createNotice(entryId, obstruction._id, {
          deadline: values.deadline ? values.deadline.format("YYYY-MM-DD") : "",
          signer: values.signer
        });
        onChanged(res.data.data);
      } else {
        const res = await emailNotice(entryId, obstruction._id, {
          email: values.email,
          message: values.message
        });
        Success(t("The notice was sent."));
        onChanged(res.data.data);
      }
      setDialog(null);
    } catch (error) {
      generalErrorAlert(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Space wrap className="sdi-notice-actions">
        {notice?.number ? (
          <>
            <Tag color="processing">
              {title} {notice.number}
            </Tag>
            {notice.sentAt && (
              <Tag color="success">
                {t("Sent to {{email}} on {{date}}", {
                  email: notice.sentTo,
                  date: dateTime(notice.sentAt)
                })}
              </Tag>
            )}
            <Button size="small" icon={<FilePdfOutlined />} onClick={openPdf}>
              {t("Open letter")}
            </Button>
            <Button
              size="small"
              icon={<MailOutlined />}
              onClick={() => {
                form.setFieldsValue({ email: clientEmail, message: "" });
                setDialog("email");
              }}
            >
              {t("Email to the client")}
            </Button>
          </>
        ) : (
          <Button
            size="small"
            icon={<FilePdfOutlined />}
            disabled={disabled}
            onClick={() => {
              form.setFieldsValue({ deadline: dayjs().add(7, "day"), signer: "" });
              setDialog("create");
            }}
          >
            {t("Create {{title}}", { title })}
          </Button>
        )}
      </Space>
      <Modal
        rootClassName="site-diary-page"
        open={Boolean(dialog)}
        onCancel={() => setDialog(null)}
        title={dialog === "create" ? t("Create {{title}}", { title }) : t("Email to the client")}
        width={560}
        destroyOnHidden
        footer={[
          <Button key="cancel" onClick={() => setDialog(null)}>
            {t("Cancel")}
          </Button>,
          <Button key="ok" type="primary" loading={busy} onClick={submit}>
            {dialog === "create" ? t("Create letter") : t("Send")}
          </Button>
        ]}
      >
        <Form form={form} layout="vertical">
          {dialog === "create" ? (
            <>
              <Alert
                type="warning"
                showIcon
                className="sdi-legal"
                message={t("Template wording")}
                description={t(
                  "The letter text is a template and not legal advice. Have it checked by your lawyer before you use it, send the notice without delay and keep proof that it was received."
                )}
              />
              <Form.Item name="deadline" label={t("Answer requested by")}>
                <DatePicker
                  format="DD.MM.YYYY"
                  className="sdi-full"
                  inputReadOnly
                  disabledDate={date => !date.isAfter(dayjs(), "day")}
                />
              </Form.Item>
              <Form.Item name="signer" label={t("Signed by (name under the letter)")}>
                <Input maxLength={120} />
              </Form.Item>
              <Typography.Paragraph type="secondary" className="sdi-small">
                {t("The letter gets a number and is kept unchanged in your files.")}
              </Typography.Paragraph>
            </>
          ) : (
            <>
              <Form.Item
                name="email"
                label={t("Email address")}
                rules={[
                  { required: true, type: "email", message: t("Please enter an email address.") }
                ]}
              >
                <Input type="email" inputMode="email" />
              </Form.Item>
              <Form.Item name="message" label={t("Message (optional)")}>
                <Input.TextArea autoSize={{ minRows: 3, maxRows: 8 }} maxLength={5000} />
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>
    </>
  );
};

export const ObstructionsCard = ({
  entryId,
  obstructions,
  editable,
  canNotice,
  dirty,
  clientEmail,
  voiceConfigured,
  voiceTranslateTo,
  onChange,
  onEntry
}) => {
  const { t } = useTranslation("dashboard");
  const rows = obstructions || [];
  const update = (index, patch) =>
    onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  if (!editable && !rows.length) return null;

  return (
    <Card
      className="sdi-card"
      size="small"
      title={
        <Space size={6}>
          <WarningOutlined /> {t("Obstructions and concerns")}
        </Space>
      }
    >
      {rows.map((row, index) => (
        <div key={row._id || `new-${index}`} className="sdi-obstruction">
          {editable ? (
            <>
              <Segmented
                block
                value={row.kind}
                disabled={Boolean(row.notice?.number)}
                onChange={kind => update(index, { kind })}
                options={[
                  { value: "OBSTRUCTION", label: t("Obstruction (§ 6 VOB/B)") },
                  { value: "CONCERN", label: t("Concerns (§ 4 Abs. 3 VOB/B)") }
                ]}
              />
              <label className="sdi-label">{t("What is the problem?")}</label>
              <DictationField
                value={row.description || ""}
                onChange={value => update(index, { description: value })}
                voiceConfigured={voiceConfigured}
                voiceTranslateTo={voiceTranslateTo}
                minRows={2}
              />
              <label className="sdi-label">
                {row.kind === "CONCERN" ? t("Why do you have concerns?") : t("Cause")}
              </label>
              <Input.TextArea
                autoSize={{ minRows: 1, maxRows: 6 }}
                maxLength={5000}
                value={row.cause}
                onChange={event => update(index, { cause: event.target.value })}
              />
              <label className="sdi-label">
                {row.kind === "CONCERN"
                  ? t("Possible consequences and your proposal")
                  : t("Consequences for the schedule and costs")}
              </label>
              <Input.TextArea
                autoSize={{ minRows: 1, maxRows: 6 }}
                maxLength={5000}
                value={row.consequences}
                onChange={event => update(index, { consequences: event.target.value })}
              />
              <Space wrap className="sdi-obstruction-foot">
                <Checkbox
                  checked={Boolean(row.noticeRequested)}
                  onChange={event => update(index, { noticeRequested: event.target.checked })}
                >
                  {t("A written notice to the client is needed")}
                </Checkbox>
                {!row.notice?.number && (
                  <Button
                    danger
                    type="text"
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={() => onChange(rows.filter((_, i) => i !== index))}
                  >
                    {t("Remove")}
                  </Button>
                )}
              </Space>
            </>
          ) : (
            <>
              <Typography.Text strong>
                {row.kind === "CONCERN" ? t("Concerns") : t("Obstruction")}:
              </Typography.Text>{" "}
              <span className="sdi-pre">{row.description}</span>
              {row.cause && (
                <div className="sdi-pre">
                  {t("Cause")}: {row.cause}
                </div>
              )}
              {row.consequences && (
                <div className="sdi-pre">
                  {t("Consequences")}: {row.consequences}
                </div>
              )}
            </>
          )}
          {canNotice && row._id && (row.noticeRequested || row.notice?.number) && (
            <NoticeActions
              entryId={entryId}
              obstruction={row}
              clientEmail={clientEmail}
              disabled={dirty}
              onChanged={onEntry}
            />
          )}
          {canNotice && row._id && row.noticeRequested && !row.notice?.number && dirty && (
            <Typography.Text type="secondary" className="sdi-small">
              {t("Save the day first.")}
            </Typography.Text>
          )}
        </div>
      ))}
      {editable && (
        <Button
          icon={<PlusOutlined />}
          className="sdi-add"
          onClick={() =>
            onChange([
              ...rows,
              { kind: "OBSTRUCTION", description: "", cause: "", consequences: "" }
            ])
          }
        >
          {t("Add obstruction or concern")}
        </Button>
      )}
    </Card>
  );
};

// ---------------------------------------------------------------------------------------------
// Photos
// ---------------------------------------------------------------------------------------------

export const PhotosCard = ({ entry, editable, captions, onCaption, onEntry }) => {
  const { t } = useTranslation("dashboard");
  const camera = useRef(null);
  const gallery = useRef(null);
  const [uploading, setUploading] = useState(0);
  const [picker, setPicker] = useState(null);
  const [chosen, setChosen] = useState([]);

  const upload = async files => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setUploading(list.length);
    try {
      let latest = null;
      for (const file of list) {
        const { blob, width, height } = await shrinkPhoto(file);
        const res = await uploadPhoto(entry._id, blob, { width, height });
        latest = res.data.data;
        setUploading(n => n - 1);
      }
      if (latest) onEntry(latest);
    } catch (error) {
      generalErrorAlert(error);
    } finally {
      setUploading(0);
    }
  };

  const openPicker = async () => {
    try {
      const res = await listSiteDocPhotos(entry._id);
      setChosen([]);
      setPicker(res.data.data);
    } catch (error) {
      generalErrorAlert(error);
    }
  };

  const linkChosen = async () => {
    try {
      let latest = null;
      for (const key of chosen) {
        const [siteDoc, photo] = key.split(":");
        latest = (await linkPhoto(entry._id, { siteDoc, photo })).data.data;
      }
      if (latest) onEntry(latest);
      setPicker(null);
    } catch (error) {
      generalErrorAlert(error);
    }
  };

  const remove = async link => {
    try {
      onEntry((await unlinkPhoto(entry._id, link._id)).data.data);
    } catch (error) {
      generalErrorAlert(error);
    }
  };

  return (
    <Card
      className="sdi-card"
      size="small"
      title={
        <Space size={6}>
          <CameraOutlined /> {t("Photos")}
        </Space>
      }
    >
      {entry.photos.length ? (
        <div className="sdi-photo-grid">
          <Image.PreviewGroup>
            {entry.photos.map(link => (
              <figure key={link._id}>
                <Image src={photoUrl(entry._id, link.siteDoc, link.photo)} alt="" />
                {editable ? (
                  <Space.Compact className="sdi-full">
                    <Input
                      size="small"
                      placeholder={t("Caption")}
                      value={captions[link._id] ?? link.caption}
                      onChange={event => onCaption(link._id, event.target.value)}
                    />
                    <Button
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      aria-label={t("Remove")}
                      onClick={() => remove(link)}
                    />
                  </Space.Compact>
                ) : (
                  link.caption && <figcaption>{link.caption}</figcaption>
                )}
              </figure>
            ))}
          </Image.PreviewGroup>
        </div>
      ) : (
        <Typography.Paragraph type="secondary">{t("No photos for this day.")}</Typography.Paragraph>
      )}
      {editable && (
        <Space wrap className="sdi-photo-actions">
          <input
            ref={camera}
            type="file"
            accept="image/*"
            capture="environment"
            hidden
            onChange={event => {
              upload(event.target.files);
              event.target.value = "";
            }}
          />
          <input
            ref={gallery}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            hidden
            onChange={event => {
              upload(event.target.files);
              event.target.value = "";
            }}
          />
          <Button
            type="primary"
            icon={<CameraOutlined />}
            loading={uploading > 0}
            onClick={() => camera.current?.click()}
          >
            {uploading > 0 ? t("Uploading… {{n}}", { n: uploading }) : t("Take photo")}
          </Button>
          <Button icon={<PictureOutlined />} onClick={() => gallery.current?.click()}>
            {t("From gallery")}
          </Button>
          <Button icon={<LinkOutlined />} onClick={openPicker}>
            {t("From site documentation")}
          </Button>
        </Space>
      )}
      <Typography.Paragraph type="secondary" className="sdi-small">
        {t("Photos are kept in the site documentation of this day.")}
      </Typography.Paragraph>
      <Modal
        rootClassName="site-diary-page"
        open={Boolean(picker)}
        onCancel={() => setPicker(null)}
        title={t("Photos of this day from the site documentation")}
        width={720}
        footer={[
          <Button key="cancel" onClick={() => setPicker(null)}>
            {t("Cancel")}
          </Button>,
          <Button key="ok" type="primary" disabled={!chosen.length} onClick={linkChosen}>
            {t("Add {{n}} photos", { n: chosen.length })}
          </Button>
        ]}
      >
        {picker?.length ? (
          <Checkbox.Group value={chosen} onChange={setChosen} className="sdi-full">
            <div className="sdi-photo-grid">
              {picker.map(item => (
                <figure key={`${item.siteDoc}:${item.photo}`}>
                  <img src={photoUrl(entry._id, item.siteDoc, item.photo)} alt="" loading="lazy" />
                  <Checkbox value={`${item.siteDoc}:${item.photo}`} disabled={item.linked}>
                    <span className="sdi-small">
                      {item.linked ? t("Already added") : item.caption || item.label}
                    </span>
                  </Checkbox>
                </figure>
              ))}
            </div>
          </Checkbox.Group>
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t("No site documentation photos of this project on this day.")}
          />
        )}
      </Modal>
    </Card>
  );
};

// ---------------------------------------------------------------------------------------------
// Signatures
// ---------------------------------------------------------------------------------------------

export const SignaturesCard = ({ entry, dirty, onSign }) => {
  const { t } = useTranslation("dashboard");
  const [role, setRole] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const { siteManager, client } = entry.signatures || {};

  const submit = async values => {
    setSubmitting(true);
    try {
      await onSign({ role, name: values.name, image: values.image });
      setRole(null);
    } finally {
      setSubmitting(false);
    }
  };

  const signature = (label, value) => (
    <div className="sdi-signature">
      <div className="sdi-label">{label}</div>
      {value?.signedAt ? (
        <>
          <img src={value.image} alt="" />
          <div className="sdi-small">
            {value.name} · {dateTime(value.signedAt)}
          </div>
        </>
      ) : (
        <Typography.Text type="secondary">{t("Not signed")}</Typography.Text>
      )}
    </div>
  );

  return (
    <Card
      className="sdi-card"
      size="small"
      title={
        <Space size={6}>
          <EditOutlined /> {t("Signatures")}
        </Space>
      }
    >
      <div className="sdi-signatures">
        {signature(t("Site manager"), siteManager)}
        {signature(t("Acknowledged by the client"), client)}
      </div>
      <Space wrap>
        {entry.can.sign && (
          <Button type="primary" disabled={dirty} onClick={() => setRole("SITE_MANAGER")}>
            {t("Sign as site manager")}
          </Button>
        )}
        {entry.can.acknowledge && (
          <Button disabled={dirty} onClick={() => setRole("CLIENT")}>
            {t("Client acknowledgment")}
          </Button>
        )}
      </Space>
      {dirty && (entry.can.sign || entry.can.acknowledge) && (
        <Typography.Paragraph type="secondary" className="sdi-small">
          {t("Save the day first.")}
        </Typography.Paragraph>
      )}
      <Modal
        rootClassName="site-diary-page"
        rootClassName="work-reports-page site-diary-page"
        open={Boolean(role)}
        onCancel={() => setRole(null)}
        title={
          role === "CLIENT" ? t("Acknowledgment by the client") : t("Signature of the site manager")
        }
        width={640}
        footer={null}
        destroyOnHidden
        maskClosable={false}
      >
        <Typography.Paragraph type="secondary">
          {role === "CLIENT"
            ? t("The client confirms having seen this day's entry (not that it is correct).")
            : t(
                "Signing locks the day. Later changes are only possible as a correction with a reason."
              )}
        </Typography.Paragraph>
        <SignatureForm
          showCopy={false}
          showLocation={false}
          submitting={submitting}
          onSubmit={submit}
          onBack={() => setRole(null)}
        />
      </Modal>
    </Card>
  );
};

// ---------------------------------------------------------------------------------------------
// History
// ---------------------------------------------------------------------------------------------

const ACTIONS = {
  create: "Started",
  lock: "Locked",
  sign: "Signed by the site manager",
  acknowledge: "Acknowledged by the client",
  correct: "Corrected",
  notice: "Notice created",
  notice_email: "Notice sent",
  email: "Sent as PDF"
};

export const HistoryCard = ({ entry }) => {
  const { t } = useTranslation("dashboard");
  const items = [...(entry.history || [])].reverse();
  return (
    <Card className="sdi-card" size="small" title={t("History")}>
      <List
        size="small"
        dataSource={items}
        renderItem={item => (
          <List.Item>
            <div>
              <Typography.Text strong>{t(ACTIONS[item.action] || item.action)}</Typography.Text>{" "}
              <Typography.Text type="secondary">
                {dateTime(item.at)}
                {item.byName ? ` · ${item.byName}` : ""}
              </Typography.Text>
              {item.note && item.action !== "lock" && <div className="sdi-pre">{item.note}</div>}
              {item.hashAfter && <div className="sdi-hash">SHA-256 {item.hashAfter}</div>}
            </div>
          </List.Item>
        )}
      />
    </Card>
  );
};
